// One durable journal per tenant / cashier / branch. Web Locks serialize tabs.
// A failed write is fatal: never issue a successful offline receipt without storage.
import { getActiveStore, isMasterView } from './scope';

const PREFIX = 'pos_shift_journal_v1:';
const DEVICE = 'pos_cashier_device_v1';
export const networkFailure = e => (!e?.response && !!(e?.isAxiosError || e?.request)) || [502, 503, 504].includes(e?.response?.status);
const message = e => e?.response?.data?.detail || e?.message || 'Sinkronisasi belum selesai';
const uid = () => crypto.randomUUID();

export function shiftScope(tenant, user) {
  if (!tenant?.id || tenant.kind !== 'tenant' || !user?.id || !getActiveStore()) throw new Error('Masuk online dan pilih cabang dahulu sebelum memakai mode offline.');
  return { tenant: tenant.id, user: user.id, store: getActiveStore(), token: localStorage.getItem('pos_token') };
}
const key = s => PREFIX + [s.tenant, s.user, s.store].join(':');
export function readJournal(s) {
  if (!s) return { shift: null, queue: [] };
  const raw = localStorage.getItem(key(s));
  if (!raw) return { version: 1, shift: null, queue: [], error: '' };
  let data;
  try { data = JSON.parse(raw); } catch { throw new Error('Data sif lokal tidak terbaca. Jangan hapus data browser; hubungi admin.'); }
  if (data.version !== 1 || !Array.isArray(data.queue)) throw new Error('Versi data sif lokal tidak sesuai. Hubungi admin.');
  return data;
}
export function draftAlreadySubmitted(tenant, user, draftId) {
  const j = readJournal({ tenant, user, store: getActiveStore() });
  return (j.submitted_drafts || []).includes(draftId);
}
function save(s, data) {
  try { localStorage.setItem(key(s), JSON.stringify(data)); }
  catch { const error = new Error('Penyimpanan perangkat penuh/tidak tersedia. Data belum aman disimpan; jangan lanjutkan pembayaran.'); error.storageFailure = true; throw error; }
  window.dispatchEvent(new Event('pos-journal-change'));
}
export function deviceId() {
  let id = localStorage.getItem(DEVICE);
  if (!id) { id = uid(); localStorage.setItem(DEVICE, id); }
  return id;
}
function guard(s) {
  if (isMasterView() || !s?.token || localStorage.getItem('pos_token') !== s.token || getActiveStore() !== s.store) throw new Error('Akun/cabang berubah. Buka kembali kasir dengan akun asal.');
  const legacy = JSON.parse(localStorage.getItem('pos_offline_txs') || '[]');
  if (legacy.length) throw new Error('Ada antrean offline versi lama tanpa identitas sif. Hubungi admin untuk rekonsiliasi; data lama tidak dihapus atau dikirim ke sif lain.');
}
async function locked(s, fn) {
  if (!navigator.locks?.request) throw new Error('Browser belum mendukung pengamanan sif offline. Gunakan Chrome terbaru melalui HTTPS.');
  return navigator.locks.request('pos-cashier-journal', async () => { guard(s); return fn(); });
}
function options(s, extra = {}) {
  guard(s);
  return { ...extra, timeout: 20000, headers: { ...extra.headers, 'X-Store-Id': s.store } };
}
async function syncLocked(api, s, j) {
  if (!navigator.onLine) throw new Error('Masih offline. Data tetap tersimpan di perangkat.');
  const responses = {};
  try {
    if (j.shift?.pending_open) {
      const { data } = await api.post('/shifts/open', j.shift.open_payload, options(s));
      if (data.status !== 'open') throw new Error('Sif asal sudah ditutup. Data lokal ditahan untuk rekonsiliasi admin.');
      j.shift = { ...j.shift, ...data, pending_open: false };
      save(s, j);
    }
    while (j.queue.length) {
      const entry = j.queue[0];
      const body = { ...entry.body, shift_id: j.shift.id, device_id: j.shift.device_id };
      const { data } = await api.post('/transactions', body, options(s, entry.approval));
      responses[entry.id] = data;
      j.queue.shift(); j.error = '';
      save(s, j); // Only remove after server confirms commit. Retried IDs remain stable.
    }
    j.error = ''; j.blocked = false; save(s, j);
    return responses;
  } catch (error) {
    j.error = String(message(error)); j.blocked = !networkFailure(error); save(s, j);
    throw error;
  }
}
export const syncShiftJournal = (api, s) => locked(s, async () => {
  const j = readJournal(s);
  await syncLocked(api, s, j);
  return j.shift;
});
export const loadDurableShift = (api, s) => locked(s, async () => {
  const j = readJournal(s);
  if (j.close_payload && navigator.onLine) {
    try {
      const { data } = await api.post('/shifts/close', j.close_payload, options(s));
      j.shift = { ...j.shift, ...data }; delete j.close_payload; save(s, j); return null;
    } catch (e) {
      if (!networkFailure(e)) { delete j.close_payload; save(s, j); throw e; }
      return j.shift;
    }
  }
  if (j.shift?.pending_open || j.queue.length || !navigator.onLine) return j.shift?.status === 'open' ? j.shift : null;
  try {
    const { data } = await api.get('/shifts/active', options(s));
    if (!data) { j.shift = null; j.error = ''; save(s, j); return null; }
    const device = deviceId();
    if (data.device_id && data.device_id !== device) throw new Error('Sif aktif berada di perangkat/alamat POS lain. Gunakan perangkat asal sampai sif ditutup.');
    const claimed = data.device_id ? data : (await api.post('/shifts/claim', { shift_id: data.id, device_id: device }, options(s))).data;
    j.shift = { ...claimed, local_id: j.shift?.id === claimed.id ? j.shift.local_id : uid() };
    save(s, j); return j.shift;
  } catch (e) {
    if (networkFailure(e) && !e.message?.startsWith('Sif aktif')) return j.shift?.status === 'open' ? j.shift : null;
    throw e;
  }
});
export const openDurableShift = (api, s, openingCash) => locked(s, async () => {
  const j = readJournal(s);
  if (j.shift?.status === 'open' || j.queue.length) throw new Error('Masih ada sif/antrean sebelumnya. Sinkronkan dahulu.');
  if (!Number.isFinite(openingCash) || openingCash < 0 || openingCash > 1e11) throw new Error('Saldo awal tidak valid');
  const id = uid();
  const payload = { opening_cash: openingCash, client_request_id: id, device_id: deviceId(), opened_at: new Date().toISOString() };
  j.shift = { ...payload, id: null, local_id: id, status: 'open', pending_open: true, open_payload: payload };
  delete j.close_payload; j.closing = false; j.error = ''; save(s, j);
  if (navigator.storage?.persist) navigator.storage.persist().catch(() => {});
  if (navigator.onLine) {
    try { await syncLocked(api, s, j); }
    catch (e) { if (!networkFailure(e)) throw e; }
  }
  return j.shift;
});
export const submitDurableSale = (api, s, shift, body, approval = {}) => locked(s, async () => {
  const j = readJournal(s);
  if (!j.shift || j.shift.local_id !== shift.local_id || j.shift.status !== 'open' || j.close_payload || j.closing) throw new Error('Sif berubah/sedang dihitung. Buka Tutup Sif lalu selesaikan atau batalkan penghitungan.');
  if (j.blocked) throw new Error('Sinkronisasi ditahan: ' + j.error + '. Selesaikan masalah ini sebelum menerima pembayaran berikutnya.');
  const old = j.queue.find(e => e.id === body.client_request_id);
  const freshOnlineAttempt = !old && j.queue.length === 0 && !j.shift.pending_open && navigator.onLine;
  if (!old) {
    if ((j.submitted_drafts || []).includes(body.client_request_id)) throw new Error('Keranjang ini sudah dicatat. Jangan menerima pembayaran kedua; periksa transaksi/antrean.');
    j.queue.push({ id: body.client_request_id, body: { ...body, occurred_at: new Date().toISOString() }, approval });
    j.submitted_drafts = [...(j.submitted_drafts || []), body.client_request_id].slice(-2000);
    save(s, j); // Must succeed BEFORE sending or acknowledging a sale.
  }
  let result;
  if (navigator.onLine) {
    try { result = (await syncLocked(api, s, j))[body.client_request_id]; }
    catch (e) {
      if (e.storageFailure) throw e;
      // A definite validation/auth rejection of a fresh online payment is not a sale.
      // Ambiguous timeouts, conflicts and previously accepted offline sales stay queued.
      if (freshOnlineAttempt && ([400, 401, 403, 422, 428].includes(e.response?.status) || (body.checkout_queue_id && e.response?.status === 409))) {
        j.queue = j.queue.filter(entry => entry.id !== body.client_request_id);
        j.submitted_drafts = j.submitted_drafts.filter(id => id !== body.client_request_id);
        j.error = ''; j.blocked = false; save(s, j); throw e;
      }
    }
  }
  return { transaction: result, pending: j.queue.length, error: j.error };
});
export const prepareDurableClose = (api, s) => locked(s, async () => {
  const j = readJournal(s);
  if (!navigator.onLine) throw new Error('Tutup sif memerlukan internet. Sif dan transaksi offline tetap tersimpan.');
  await syncLocked(api, s, j);
  if (!j.shift?.id || j.shift.status !== 'open') throw new Error('Tidak ada sif aktif');
  j.closing = true; save(s, j);
  return j.shift;
});
export const cancelDurableClose = s => locked(s, async () => {
  const j = readJournal(s);
  if (!j.close_payload) { j.closing = false; save(s, j); }
});
export const closeDurableShift = (api, s, shift, body, approval) => locked(s, async () => {
  const j = readJournal(s);
  if (!navigator.onLine) throw new Error('Tutup sif memerlukan internet');
  if (!j.shift || j.shift.id !== shift.id) throw new Error('Sif berubah, periksa ulang');
  await syncLocked(api, s, j);
  const payload = { ...body, shift_id: shift.id, device_id: j.shift.device_id };
  if (j.close_payload && JSON.stringify(j.close_payload) !== JSON.stringify(payload)) throw new Error('Penutupan sebelumnya belum terkonfirmasi. Gunakan rincian hitung kas yang sama.');
  j.close_payload = payload; save(s, j);
  try {
    const { data } = await api.post('/shifts/close', payload, options(s, approval));
    j.shift = { ...j.shift, ...data }; delete j.close_payload; j.closing = false; j.error = ''; save(s, j);
    return data;
  } catch (e) {
    if (e.response && !networkFailure(e)) { delete j.close_payload; save(s, j); }
    throw e;
  }
});
